import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { FCanvasComponent } from '@foblex/flow';
import * as dagre from 'dagre';
import { firstValueFrom, Observable, of } from 'rxjs';
import { CALL_ACTION_TYPES, CONDITIONAL_ACTION_TYPES, DialPlanAction, DialPlanActionTypeValue } from '../../_types/dial-plan-action';
import { DialPlanActionBranch } from '../../_types/dial-plan-action-branch';
import { DialPlanActionNode, DialPlanBranchNode, DialPlansService } from '../../_services/dial-plans.service';
import { DialPlanActionsService } from '../../_services/dial-plan-actions.service';
import { DialPlanActionBranchesService } from '../../_services/dial-plan-action-branches.service';
import { QueuesService } from '../../_services/queues.service';
import { DialPlanActionEditComponent } from '../dial-plan-action-edit/dial-plan-action-edit.component';

interface FlowNodeBase {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ActionFlowNode extends FlowNodeBase {
  kind: 'action';
  action: DialPlanActionNode | null;
  parentBranchId: string | null;
}

export interface BranchFlowNode extends FlowNodeBase {
  kind: 'branch';
  branch: DialPlanBranchNode;
  parentActionId: string;
}

export interface AddSlotFlowNode extends FlowNodeBase {
  kind: 'add';
  parentBranchId: string | null;
  sortOrder: number;
}

export interface AddBranchSlotFlowNode extends FlowNodeBase {
  kind: 'add-branch';
  parentAction: DialPlanActionNode;
}

type FlowNode = ActionFlowNode | BranchFlowNode | AddSlotFlowNode | AddBranchSlotFlowNode;

interface FlowEdge {
  id: string;
  source: string;
  target: string;
  insertAt?: { parentBranchId: string | null; sortOrder: number };
  // arista de enlace "Ir a" (GoTo): conecta una salida con una acción existente
  isLink?: boolean;
  gotoId?: string;
}

interface ActionStyle {
  icon: string;
  color: string;
  label: string;
  subtitle: string | ((a: DialPlanActionNode) => string);
}

const NODE_WIDTH = 240;
const NODE_HEIGHT = 64;
const BRANCH_WIDTH = 84;
const BRANCH_HEIGHT = 32;
const ADD_SIZE = 54;

@Component({
    selector: 'app-dial-plan-flow',
    templateUrl: './dial-plan-flow.component.html',
    styleUrls: ['./dial-plan-flow.component.scss'],
    standalone: false
})
export class DialPlanFlowComponent implements OnChanges {
  @Input() rootActions: DialPlanActionNode[] = [];
  @Input() dialPlanId!: string;
  @Output() changed = new EventEmitter<void>();

  // árboles internos: working = lo que renderizo (mutable), original = snapshot del servidor
  private originalTree: DialPlanActionNode[] = [];
  private workingTree: DialPlanActionNode[] = [];

  @ViewChild(FCanvasComponent) fCanvas?: FCanvasComponent;
  private flowLoaded = false;
  private initialFitDone = false;

  // catalogo de colas para mostrar nombres en los nodos
  private queueNameById = new Map<string, string>();

  public actionNodes: ActionFlowNode[] = [];
  public branchNodes: BranchFlowNode[] = [];
  public addNodes: AddSlotFlowNode[] = [];
  public addBranchNodes: AddBranchSlotFlowNode[] = [];
  public edges: FlowEdge[] = [];

  public addingBranchForAction: DialPlanActionNode | null = null;
  public newBranchDigit: string | null = null;

  // modo mover: acción que se está reubicando junto con su subárbol
  public movingAction: DialPlanActionNode | null = null;
  private movingFromBranchId: string | null = null;
  private movingBranchIds = new Set<string>();

  // elección al pulsar "+": nueva acción o conectar a existente
  public slotChoice: { parentBranchId: string | null; sortOrder: number } | null = null;

  // modo conectar: slot de origen del enlace GoTo a crear; al clicar una acción se enlaza
  public linkingFrom: { parentBranchId: string | null; sortOrder: number } | null = null;

  private actionById = new Map<string, DialPlanActionNode>();

  public styles: Record<string, ActionStyle> = {
    Start: {
      icon: 'pi pi-phone',
      color: '#10b981',
      label: 'Llamada entrante',
      subtitle: 'Inicio del plan'
    },
    CallQueue: {
      icon: 'pi pi-users',
      color: '#3b82f6',
      label: 'Llamar a cola',
      subtitle: a => a.queueId
        ? (this.queueNameById.get(a.queueId) ?? a.queueId.substring(0, 8))
        : '(cola no definida)'
    },
    CallExtension: {
      icon: 'pi pi-phone',
      color: '#06b6d4',
      label: 'Llamar a extensión',
      subtitle: a => {
        if (!a.extensionNumber) return '(extensión no definida)';
        const base = `Ext. ${a.extensionNumber}`;
        return a.application ? `${base} · ${a.application}` : base;
      }
    },
    PlayAudio: {
      icon: 'pi pi-volume-up',
      color: '#a855f7',
      label: 'Reproducir audio',
      subtitle: a => a.audioFilename || '(audio no definido)'
    },
    WaitForInput: {
      icon: 'pi pi-th-large',
      color: '#8b5cf6',
      label: 'Esperar pulsación',
      subtitle: a => `Timeout ${a.timeoutSeconds ?? '-'}s · ${a.maxDigits ?? 1} dígito(s)`
    },
    IsBusinessHours: {
      icon: 'pi pi-clock', color: '#3b82f6', label: 'Horario normal',
      subtitle: '¿Está dentro del horario?'
    },
    IsOnCallHours: {
      icon: 'pi pi-shield', color: '#3b82f6', label: 'Horario de guardia',
      subtitle: '¿Está en horario de guardia?'
    },
    CallerIsWorker: {
      icon: 'pi pi-user', color: '#f97316', label: 'Llamante operario',
      subtitle: '¿El llamante es operario?'
    },
    CallerIsClient: {
      icon: 'pi pi-user', color: '#f97316', label: 'Llamante cliente',
      subtitle: '¿El llamante es cliente?'
    },
    CallerIsCompany: {
      icon: 'pi pi-building', color: '#f97316', label: 'Llamante compañía',
      subtitle: '¿El llamante es compañía?'
    },
    CallerIsExpert: {
      icon: 'pi pi-id-card', color: '#f97316', label: 'Llamante perito',
      subtitle: '¿El llamante es perito?'
    },
    CallCaseProcessor: {
      icon: 'pi pi-user-edit', color: '#ec4899', label: 'Tramitador del expediente',
      subtitle: 'Llamar al tramitador'
    },
    CallCaseContractQueue: {
      icon: 'pi pi-briefcase', color: '#3b82f6', label: 'Cola del contrato',
      subtitle: 'Llamar a cola del contrato'
    },
    EndCall: {
      icon: 'pi pi-phone', color: '#ef4444', label: 'Finalizar llamada',
      subtitle: 'Colgar la llamada'
    },
    Wait: {
      icon: 'pi pi-hourglass', color: '#0ea5e9', label: 'Esperar',
      subtitle: a => `${a.waitSeconds ?? '-'} s`
    },
    SendDtmf: {
      icon: 'pi pi-th-large', color: '#0ea5e9', label: 'Enviar pulsación',
      subtitle: a => a.dtmfDigit ? `Pulsa "${a.dtmfDigit}"` : '(sin pulsación)'
    },
    ReadText: {
      icon: 'pi pi-comment', color: '#a855f7', label: 'Leer texto (TTS)',
      subtitle: a => a.ttsText ? (a.ttsText.length > 28 ? a.ttsText.substring(0, 28) + '…' : a.ttsText) : '(sin texto)'
    },
    SetCallerId: {
      icon: 'pi pi-id-card', color: '#ec4899', label: 'Establecer Caller ID',
      subtitle: a => a.callerIdName ? `"${a.callerIdName}"` : '(sin nombre)'
    },
    CallerNumberIs: {
      icon: 'pi pi-phone', color: '#f97316', label: 'Número que llama es',
      subtitle: a => a.callerNumber ? `¿Es ${a.callerNumber}?` : '(número no definido)'
    },
    CumpleHorario: {
      icon: 'pi pi-calendar', color: '#06b6d4', label: 'Cumple horario',
      subtitle: a => (a.startTime && a.endTime && a.daysOfWeek)
        ? `${a.startTime}–${a.endTime} · ${this.daysOfWeekLabel(a.daysOfWeek)}`
        : '(horario no definido)'
    },
    IsHoliday: {
      icon: 'pi pi-calendar-times', color: '#3b82f6', label: 'Es festivo',
      subtitle: '¿Hoy es festivo?'
    }
  };

  /** Convierte CSV ISO de días ("1,2,3,4,5") a abreviaturas ("L,M,X,J,V"). */
  daysOfWeekLabel(daysOfWeek: string): string {
    const labels: Record<string, string> = {
      '1': 'L', '2': 'M', '3': 'X', '4': 'J', '5': 'V', '6': 'S', '7': 'D'
    };
    return daysOfWeek
      .split(',')
      .map(d => d.trim())
      .filter(d => d)
      .map(d => labels[d] ?? d)
      .join(',');
  }

  subtitleFor(action: DialPlanActionNode | null): string {
    const style = this.styleFor(action);
    if (typeof style.subtitle === 'string') return style.subtitle;
    return action ? style.subtitle(action) : '';
  }

  public digitOptions = [
    { label: '0', value: '0' }, { label: '1', value: '1' }, { label: '2', value: '2' },
    { label: '3', value: '3' }, { label: '4', value: '4' }, { label: '5', value: '5' },
    { label: '6', value: '6' }, { label: '7', value: '7' }, { label: '8', value: '8' },
    { label: '9', value: '9' }, { label: '*', value: '*' }, { label: '#', value: '#' },
    { label: 'Timeout', value: 'timeout' }, { label: 'Inválido', value: 'invalid' }
  ];

  public yesNoOptions = [
    { label: 'Sí', value: 'yes' },
    { label: 'No', value: 'no' }
  ];

  public callOptions = [
    { label: 'Descolgado', value: 'answered' },
    { label: 'No descolgado', value: 'noanswer' }
  ];

  constructor(
    private cdr: ChangeDetectorRef,
    private dialPlansService: DialPlansService,
    private actionsService: DialPlanActionsService,
    private branchesService: DialPlanActionBranchesService,
    private queuesService: QueuesService,
    private dialogService: DialogService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {
    this.loadQueueNames();
  }

  private async loadQueueNames() {
    try {
      const response = await firstValueFrom(this.queuesService.all({ page: { number: 1, size: 500 }, sort: ['name'] }));
      this.queueNameById.clear();
      (response.data as any[]).forEach(q => this.queueNameById.set(q.id, q.name));
      this.cdr.detectChanges();
    } catch {
      // si falla, los subtítulos mostrarán el id
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['rootActions']) {
      this.originalTree = this.deepClone(this.rootActions);
      this.workingTree = this.deepClone(this.rootActions);
      // nuevo árbol = nuevo fit inicial
      this.initialFitDone = false;
      this.rebuild();
    }
  }

  private deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  private genTmpId(): string {
    return 'tmp-' + (window.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2));
  }

  private isTmp(id: string): boolean {
    return id.startsWith('tmp-');
  }

  /**
   * Persiste todos los cambios en memoria contra el servidor mediante diff
   * entre el árbol original (snapshot) y el árbol working (mutado por el usuario).
   * Devuelve un observable que completa cuando termina.
   */
  commit(): Observable<void> {
    return new Observable<void>(observer => {
      (async () => {
        try {
          const tmpToReal = new Map<string, string>();
          // 1. Creates y updates (top-down)
          await this.commitActionsLevel(this.workingTree, this.originalTree, null, tmpToReal);
          // 2. Deletes (acciones y ramas que estaban en original y ya no estan en working)
          await this.commitDeletes(this.originalTree, this.workingTree);
          // 3. Fixup de enlaces GoTo cuyo destino era una acción nueva (id temporal):
          //    ahora tmpToReal está completo y podemos parchear el targetActionId real
          await this.commitGoToTargets(this.workingTree, tmpToReal);
          observer.next();
          observer.complete();
        } catch (err) {
          observer.error(err);
        }
      })();
    });
  }

  private async commitActionsLevel(
    workingActions: DialPlanActionNode[],
    originalActions: DialPlanActionNode[],
    parentBranchId: string | null,
    tmpToReal: Map<string, string>
  ) {
    const originalById = new Map(originalActions.map(a => [a.id, a]));
    for (const action of workingActions) {
      const resolvedParentBranchId = parentBranchId && this.isTmp(parentBranchId)
        ? tmpToReal.get(parentBranchId) ?? parentBranchId
        : parentBranchId;

      if (this.isTmp(action.id)) {
        // CREATE acción
        const newEntity = new DialPlanAction();
        newEntity.dialPlanId = this.dialPlanId;
        newEntity.parentBranchId = resolvedParentBranchId ?? undefined;
        newEntity.sortOrder = action.sortOrder;
        newEntity.actionType = action.actionType;
        newEntity.queueId = action.queueId;
        newEntity.extensionNumber = action.extensionNumber;
        newEntity.application = action.application;
        newEntity.data = action.data;
        newEntity.callerId = action.callerId;
        newEntity.audioFilename = action.audioFilename;
        newEntity.timeoutSeconds = action.timeoutSeconds;
        newEntity.maxDigits = action.maxDigits;
        newEntity.waitSeconds = action.waitSeconds;
        newEntity.dtmfDigit = action.dtmfDigit;
        newEntity.ttsText = action.ttsText;
        newEntity.callerIdName = action.callerIdName;
        newEntity.callerNumber = action.callerNumber;
        newEntity.startTime = action.startTime;
        newEntity.endTime = action.endTime;
        newEntity.daysOfWeek = action.daysOfWeek;
        // solo enviamos el destino si ya es un id real; los destinos temporales se
        // parchean en commitGoToTargets() cuando se conozca su id real
        newEntity.targetActionId = this.resolveTargetId(action.targetActionId, tmpToReal);
        const saved: any = await firstValueFrom(this.actionsService.save(newEntity));
        tmpToReal.set(action.id, saved.id);
        // sus ramas son nuevas tambien
        await this.commitBranchesLevel(action.branches, [], saved.id, tmpToReal);
      } else {
        const original = originalById.get(action.id);
        // si no esta en originalById es que se movio a este nivel desde otro
        const moved = !original;
        if (moved || (original && this.actionDiffers(action, original))) {
          const partial = new DialPlanAction();
          partial.id = action.id;
          partial.sortOrder = action.sortOrder;
          partial.actionType = action.actionType;
          partial.queueId = action.queueId;
          partial.extensionNumber = action.extensionNumber;
          partial.application = action.application;
          partial.data = action.data;
          partial.callerId = action.callerId;
          partial.audioFilename = action.audioFilename;
          partial.timeoutSeconds = action.timeoutSeconds;
          partial.maxDigits = action.maxDigits;
          partial.waitSeconds = action.waitSeconds;
          partial.dtmfDigit = action.dtmfDigit;
          partial.ttsText = action.ttsText;
          partial.callerIdName = action.callerIdName;
          partial.callerNumber = action.callerNumber;
          partial.startTime = action.startTime;
          partial.endTime = action.endTime;
          partial.daysOfWeek = action.daysOfWeek;
          partial.targetActionId = this.resolveTargetId(action.targetActionId, tmpToReal);
          if (moved) {
            // tambien parchear parentBranchId para reflejar el nuevo padre
            partial.parentBranchId = resolvedParentBranchId ?? undefined;
          }
          await firstValueFrom(this.actionsService.save(partial));
        }
        await this.commitBranchesLevel(
          action.branches,
          original?.branches ?? [],
          action.id,
          tmpToReal
        );
      }
    }
  }

  private async commitBranchesLevel(
    workingBranches: DialPlanBranchNode[],
    originalBranches: DialPlanBranchNode[],
    parentActionId: string,
    tmpToReal: Map<string, string>
  ) {
    const originalById = new Map(originalBranches.map(b => [b.id, b]));
    for (const branch of workingBranches) {
      const resolvedParentActionId = this.isTmp(parentActionId)
        ? tmpToReal.get(parentActionId) ?? parentActionId
        : parentActionId;

      if (this.isTmp(branch.id)) {
        const newEntity = new DialPlanActionBranch();
        newEntity.dialPlanActionId = resolvedParentActionId;
        newEntity.digit = branch.digit;
        const saved: any = await firstValueFrom(this.branchesService.save(newEntity));
        tmpToReal.set(branch.id, saved.id);
        await this.commitActionsLevel(branch.actions, [], saved.id, tmpToReal);
      } else {
        const original = originalById.get(branch.id);
        if (original && this.branchDiffers(branch, original)) {
          const partial = new DialPlanActionBranch();
          partial.id = branch.id;
          partial.digit = branch.digit;
          await firstValueFrom(this.branchesService.save(partial));
        }
        await this.commitActionsLevel(
          branch.actions,
          original?.actions ?? [],
          branch.id,
          tmpToReal
        );
      }
    }
  }

  private async commitDeletes(
    originalActions: DialPlanActionNode[],
    workingActions: DialPlanActionNode[]
  ) {
    const workingActionIds = new Set<string>();
    const collectIds = (acts: DialPlanActionNode[]) => {
      acts.forEach(a => {
        workingActionIds.add(a.id);
        a.branches?.forEach(b => collectIds(b.actions));
      });
    };
    collectIds(workingActions);

    const workingBranchIds = new Set<string>();
    const collectBranchIds = (acts: DialPlanActionNode[]) => {
      acts.forEach(a => {
        a.branches?.forEach(b => {
          workingBranchIds.add(b.id);
          collectBranchIds(b.actions);
        });
      });
    };
    collectBranchIds(workingActions);

    // Borrar top-down: si una accion no esta en working la borramos en cascada
    // y no descendemos en ella (cascade endpoint borra hijos)
    const walkAndDelete = async (origActions: DialPlanActionNode[]) => {
      for (const a of origActions) {
        if (!workingActionIds.has(a.id)) {
          await firstValueFrom(this.dialPlansService.deleteActionCascade(a.id));
          continue;
        }
        for (const b of a.branches ?? []) {
          if (!workingBranchIds.has(b.id)) {
            await firstValueFrom(this.dialPlansService.deleteBranchCascade(b.id));
            continue;
          }
          await walkAndDelete(b.actions);
        }
      }
    };
    await walkAndDelete(originalActions);
  }

  /** Resuelve un targetActionId al id real: tmp→real si se conoce, real tal cual, undefined si aún no. */
  private resolveTargetId(targetActionId: string | undefined, tmpToReal: Map<string, string>): string | undefined {
    if (!targetActionId) return undefined;
    if (this.isTmp(targetActionId)) return tmpToReal.get(targetActionId);
    return targetActionId;
  }

  /**
   * Parchea los enlaces GoTo cuyo destino era una acción nueva (id temporal): en la creación
   * el id real del destino podía no conocerse todavía, así que aquí, con tmpToReal completo,
   * actualizamos el targetActionId definitivo.
   */
  private async commitGoToTargets(workingActions: DialPlanActionNode[], tmpToReal: Map<string, string>) {
    const gotos: DialPlanActionNode[] = [];
    const collect = (acts: DialPlanActionNode[]) => {
      acts.forEach(a => {
        if (a.actionType === 'GoTo') gotos.push(a);
        a.branches?.forEach(b => collect(b.actions));
      });
    };
    collect(workingActions);

    for (const goto of gotos) {
      if (!goto.targetActionId || !this.isTmp(goto.targetActionId)) continue;
      const realTarget = tmpToReal.get(goto.targetActionId);
      if (!realTarget) continue;
      const realGotoId = tmpToReal.get(goto.id) ?? goto.id;
      const partial = new DialPlanAction();
      partial.id = realGotoId;
      partial.targetActionId = realTarget;
      await firstValueFrom(this.actionsService.save(partial));
    }
  }

  private actionDiffers(a: DialPlanActionNode, b: DialPlanActionNode): boolean {
    return a.sortOrder !== b.sortOrder
      || a.actionType !== b.actionType
      || (a.queueId ?? null) !== (b.queueId ?? null)
      || (a.extensionNumber ?? null) !== (b.extensionNumber ?? null)
      || (a.application ?? null) !== (b.application ?? null)
      || (a.data ?? null) !== (b.data ?? null)
      || (a.callerId ?? null) !== (b.callerId ?? null)
      || (a.audioFilename ?? null) !== (b.audioFilename ?? null)
      || (a.timeoutSeconds ?? null) !== (b.timeoutSeconds ?? null)
      || (a.maxDigits ?? null) !== (b.maxDigits ?? null)
      || (a.waitSeconds ?? null) !== (b.waitSeconds ?? null)
      || (a.dtmfDigit ?? null) !== (b.dtmfDigit ?? null)
      || (a.ttsText ?? null) !== (b.ttsText ?? null)
      || (a.callerIdName ?? null) !== (b.callerIdName ?? null)
      || (a.callerNumber ?? null) !== (b.callerNumber ?? null)
      || (a.startTime ?? null) !== (b.startTime ?? null)
      || (a.endTime ?? null) !== (b.endTime ?? null)
      || (a.daysOfWeek ?? null) !== (b.daysOfWeek ?? null)
      || (a.targetActionId ?? null) !== (b.targetActionId ?? null);
  }

  private branchDiffers(a: DialPlanBranchNode, b: DialPlanBranchNode): boolean {
    return a.digit !== b.digit;
  }

  hasPendingChanges(): boolean {
    return JSON.stringify(this.workingTree) !== JSON.stringify(this.originalTree);
  }

  styleFor(action: DialPlanActionNode | null): ActionStyle {
    if (!action) return this.styles['Start'];
    return this.styles[action.actionType] ?? {
      icon: 'pi pi-circle',
      color: '#64748b',
      label: action.actionType,
      subtitle: () => ''
    };
  }

  isConditional(action: DialPlanActionNode): boolean {
    return CONDITIONAL_ACTION_TYPES.includes(action.actionType);
  }

  isCallable(action: DialPlanActionNode): boolean {
    return CALL_ACTION_TYPES.includes(action.actionType);
  }

  hasBranches(action: DialPlanActionNode): boolean {
    return action.actionType === 'WaitForInput' || this.isConditional(action) || this.isCallable(action);
  }

  isTerminal(action: DialPlanActionNode): boolean {
    return action.actionType === 'EndCall';
  }

  branchOptions(action: DialPlanActionNode) {
    return this.branchOptionsByType(action.actionType);
  }

  branchOptionsByType(actionType: DialPlanActionTypeValue) {
    if (CONDITIONAL_ACTION_TYPES.includes(actionType)) return this.yesNoOptions;
    if (CALL_ACTION_TYPES.includes(actionType)) return this.callOptions;
    return this.digitOptions;
  }

  branchLabelFor(branchNode: BranchFlowNode): string {
    const parent = this.actionById.get(branchNode.parentActionId);
    const opts = parent ? this.branchOptions(parent) : this.digitOptions;
    return opts.find(o => o.value === branchNode.branch.digit)?.label ?? branchNode.branch.digit;
  }

  trackNode = (_: number, n: FlowNodeBase) => n.id;
  trackEdge = (_: number, e: FlowEdge) => e.id;

  // -------- layout --------

  private rebuild() {
    this.actionById.clear();
    const indexActions = (acts: DialPlanActionNode[]) => {
      acts.forEach(a => {
        this.actionById.set(a.id, a);
        a.branches?.forEach(b => indexActions(b.actions));
      });
    };
    indexActions(this.workingTree);

    const g = new dagre.graphlib.Graph();
    g.setGraph({ rankdir: 'TB', nodesep: 36, ranksep: 56, marginx: 30, marginy: 30 });
    g.setDefaultEdgeLabel(() => ({}));

    const nodes: FlowNode[] = [];
    const edges: FlowEdge[] = [];
    // enlaces GoTo pendientes: se añaden tras recorrer el árbol, cuando el nodo destino ya existe
    const linkEdges: { source: string; targetActionId: string | undefined; gotoId: string }[] = [];

    const addNode = (n: FlowNode) => {
      nodes.push(n);
      g.setNode(n.id, { width: n.width, height: n.height });
    };
    const addEdge = (source: string, target: string, insertAt?: { parentBranchId: string | null; sortOrder: number }) => {
      edges.push({ id: `${source}__${target}`, source, target, insertAt });
      g.setEdge(source, target);
    };

    // nodo "Llamada entrante"
    const start: ActionFlowNode = {
      id: 'start', kind: 'action', action: null, parentBranchId: null,
      x: 0, y: 0, width: NODE_WIDTH, height: NODE_HEIGHT
    };
    addNode(start);

    const visit = (
      actions: DialPlanActionNode[],
      previousId: string,
      parentBranchId: string | null
    ) => {
      const sorted = [...actions].sort((a, b) => a.sortOrder - b.sortOrder);
      let lastId = previousId;
      let stopped = false;

      for (const action of sorted) {
        // GoTo: no se dibuja tarjeta ni "+" final; se registra un enlace desde el nodo
        // predecesor (lastId) hasta la acción destino y se corta el nivel
        if (action.actionType === 'GoTo') {
          linkEdges.push({ source: lastId, targetActionId: action.targetActionId, gotoId: action.id });
          stopped = true;
          break;
        }

        const actNode: ActionFlowNode = {
          id: 'a-' + action.id, kind: 'action', action, parentBranchId,
          x: 0, y: 0, width: NODE_WIDTH, height: NODE_HEIGHT
        };
        addNode(actNode);
        // edge con metadata para mostrar boton "+" inline para insertar antes
        addEdge(lastId, actNode.id, { parentBranchId, sortOrder: action.sortOrder });

        if (this.hasBranches(action)) {
          action.branches.forEach(branch => {
            const branchNode: BranchFlowNode = {
              id: 'b-' + branch.id, kind: 'branch', branch, parentActionId: action.id,
              x: 0, y: 0, width: BRANCH_WIDTH, height: BRANCH_HEIGHT
            };
            addNode(branchNode);
            addEdge(actNode.id, branchNode.id);

            if (branch.actions.length > 0) {
              // visit() añade el propio slot "+" final si la última acción no es ramificable
              visit(branch.actions, branchNode.id, branch.id);
            } else {
              const branchEmptyAdd: AddSlotFlowNode = {
                id: 'add-branch-' + branch.id, kind: 'add',
                parentBranchId: branch.id, sortOrder: 1,
                x: 0, y: 0, width: ADD_SIZE, height: ADD_SIZE
              };
              addNode(branchEmptyAdd);
              addEdge(branchNode.id, branchEmptyAdd.id);
            }
          });

          // slot para añadir nueva rama (solo si quedan opciones disponibles)
          const maxBranches = this.branchOptions(action).length;
          if (action.branches.length < maxBranches) {
            const addBranchSlot: AddBranchSlotFlowNode = {
              id: 'addbranch-' + action.id, kind: 'add-branch', parentAction: action,
              x: 0, y: 0, width: ADD_SIZE, height: ADD_SIZE
            };
            addNode(addBranchSlot);
            addEdge(actNode.id, addBranchSlot.id);
          }

          // las acciones que vengan despues de una ramificable no se muestran:
          // deben ir dentro de alguna rama, no como hermanas
          stopped = true;
          break;
        }

        if (this.isTerminal(action)) {
          // tras una acción terminal (EndCall) no hay continuación posible
          stopped = true;
          break;
        }

        lastId = actNode.id;
      }

      // slot "+" final solo si la secuencia no termina con una accion ramificable ni terminal
      if (!stopped) {
        const tailSortOrder = sorted.length > 0
          ? Math.max(...sorted.map(a => a.sortOrder)) + 1
          : 1;
        const tailAdd: AddSlotFlowNode = {
          id: 'add-tail-' + (parentBranchId ?? 'root') + '-' + sorted.length,
          kind: 'add',
          parentBranchId,
          sortOrder: tailSortOrder,
          x: 0, y: 0, width: ADD_SIZE, height: ADD_SIZE
        };
        addNode(tailAdd);
        addEdge(lastId, tailAdd.id);
      }
    };

    visit(this.workingTree, 'start', null);

    // aristas de enlace GoTo (el nodo destino ya está en el grafo); destinos colgantes se omiten
    linkEdges.forEach(le => {
      if (!le.targetActionId) return;
      const targetNodeId = 'a-' + le.targetActionId;
      if (!g.hasNode(targetNodeId)) return;
      edges.push({ id: 'link-' + le.gotoId, source: le.source, target: targetNodeId, isLink: true, gotoId: le.gotoId });
      g.setEdge(le.source, targetNodeId);
    });

    dagre.layout(g);
    nodes.forEach(n => {
      const layout = g.node(n.id);
      if (layout) {
        n.x = layout.x - n.width / 2;
        n.y = layout.y - n.height / 2;
      }
    });

    this.actionNodes = nodes.filter(n => n.kind === 'action') as ActionFlowNode[];
    this.branchNodes = nodes.filter(n => n.kind === 'branch') as BranchFlowNode[];
    this.addNodes = nodes.filter(n => n.kind === 'add') as AddSlotFlowNode[];
    this.addBranchNodes = nodes.filter(n => n.kind === 'add-branch') as AddBranchSlotFlowNode[];
    this.edges = edges;
    this.cdr.detectChanges();

    // solo encuadrar la primera vez para no resetear zoom/pan del usuario
    if (this.flowLoaded && !this.initialFitDone) {
      this.scheduleInitialFit();
    }
  }

  /**
   * Espera unos frames a que foblex termine de medir todos los nodos/ramas
   * antes de hacer el fit inicial — con un solo setTimeout(0) se ejecuta
   * antes de que las conexiones estén pintadas y el encuadre queda mal.
   */
  private scheduleInitialFit() {
    if (this.initialFitDone) return;
    // doble RAF + delay para garantizar que el ciclo de medida de foblex
    // (que se hace tras el primer paint) ya ha calculado tamaños de ramas
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (this.initialFitDone) return;
          this.fCanvas?.fitToScreen({ x: 40, y: 40 }, false);
          this.initialFitDone = true;
        }, 120);
      });
    });
  }

  /** Encuadre manual disparado por el botón flotante. */
  fitNow() {
    this.fCanvas?.fitToScreen({ x: 40, y: 40 }, true);
  }

  onFlowLoaded() {
    this.flowLoaded = true;
    if (!this.initialFitDone) this.scheduleInitialFit();
  }

  // -------- interacciones --------

  editAction(node: ActionFlowNode) {
    if (!node.action) return;
    if (this.linkingFrom) { this.completeLink(node.action); return; } // modo conectar: enlazar aquí
    if (this.movingAction) return; // en modo mover, el clic en la tarjeta no edita
    this.openActionDialog(node.action, node.parentBranchId, node.action.sortOrder);
  }

  onSlotClick(slot: AddSlotFlowNode) {
    if (this.movingAction) {
      if (this.isMoveTargetSlot(slot)) this.completeMove(slot.parentBranchId, slot.sortOrder);
      return;
    }
    if (this.linkingFrom) return; // en modo conectar, los "+" no actúan
    // ofrecemos elegir entre nueva acción o conectar a una existente
    this.slotChoice = { parentBranchId: slot.parentBranchId, sortOrder: slot.sortOrder };
  }

  // -------- elección del slot "+": nueva acción vs conectar --------

  chooseNewAction() {
    const choice = this.slotChoice;
    this.slotChoice = null;
    if (choice) this.openActionDialog(null, choice.parentBranchId, choice.sortOrder);
  }

  chooseConnect() {
    this.linkingFrom = this.slotChoice;
    this.slotChoice = null;
    this.cdr.detectChanges();
  }

  cancelSlotChoice() {
    this.slotChoice = null;
  }

  // -------- modo conectar (enlace GoTo) --------

  /** ¿La tarjeta es un destino válido de enlace? (cualquier acción real). */
  isLinkTarget(node: ActionFlowNode): boolean {
    return !!this.linkingFrom && !!node.action;
  }

  cancelLink() {
    this.linkingFrom = null;
    this.cdr.detectChanges();
  }

  /** Crea una acción GoTo en el slot de origen apuntando a la acción destino elegida. */
  private completeLink(target: DialPlanActionNode) {
    const from = this.linkingFrom;
    if (!from) return;
    const goto: DialPlanActionNode = {
      id: this.genTmpId(),
      sortOrder: from.sortOrder,
      actionType: 'GoTo',
      targetActionId: target.id,
      branches: []
    } as DialPlanActionNode;
    this.linkingFrom = null;
    this.insertActionIntoWorking(goto, from.parentBranchId);
  }

  /** Elimina un enlace GoTo (la arista discontinua) borrando su acción del árbol. */
  deleteLink(edge: FlowEdge) {
    if (!edge.gotoId) return;
    this.removeActionFromWorking(edge.gotoId);
    this.rebuild();
  }

  onEdgeInsertClick(edge: FlowEdge) {
    if (!edge.insertAt) return;
    if (this.movingAction) {
      if (this.isMoveTargetEdge(edge)) this.completeMove(edge.insertAt.parentBranchId, edge.insertAt.sortOrder);
      return;
    }
    this.openActionDialog(null, edge.insertAt.parentBranchId, edge.insertAt.sortOrder);
  }

  private openActionDialog(action: DialPlanActionNode | null, parentBranchId: string | null, sortOrder: number) {
    const ref = this.dialogService.open(DialPlanActionEditComponent, {
      closable: true,
      modal: true,
      data: { action, dialPlanId: this.dialPlanId, parentBranchId, nextSortOrder: sortOrder },
      header: action ? 'Editar acción' : 'Nueva acción',
      width: '700px'
    });
    ref.onClose.subscribe((data: Partial<DialPlanActionNode> | undefined) => {
      if (!data) return;
      if (action) {
        // edicion
        Object.assign(action, data);
        this.rebuild();
      } else {
        // creacion
        const newAction: DialPlanActionNode = {
          id: this.genTmpId(),
          sortOrder,
          actionType: 'CallQueue',
          branches: [],
          ...data
        } as DialPlanActionNode;
        this.insertActionIntoWorking(newAction, parentBranchId);
      }
    });
  }

  private getSiblingsAt(parentBranchId: string | null): DialPlanActionNode[] | undefined {
    if (parentBranchId === null) return this.workingTree;
    return this.findBranchInWorking(parentBranchId)?.actions;
  }

  private async insertActionIntoWorking(action: DialPlanActionNode, parentBranchId: string | null) {
    const siblings = this.getSiblingsAt(parentBranchId);
    if (!siblings) return;

    const subsequent = siblings.filter(s => s.sortOrder >= action.sortOrder);

    if (subsequent.length > 0 && this.hasBranches(action)) {
      // ramificable mid-flujo: auto-mover posteriores a una nueva rama por defecto
      const confirmed = await this.confirmAsync(
        'Las acciones posteriores se moverán dentro de una nueva rama de esta acción. ¿Continuar?',
        'Insertar acción ramificable'
      );
      if (!confirmed) return;

      const firstOption = this.branchOptionsByType(action.actionType)[0];
      const moved = subsequent.sort((a, b) => a.sortOrder - b.sortOrder).map((a, idx) => ({
        ...a, sortOrder: idx + 1
      }));
      const newBranch: DialPlanBranchNode = {
        id: this.genTmpId(),
        digit: firstOption.value,
        actions: moved
      };
      action.branches = [newBranch];

      // quitar los movidos de su sitio actual
      for (let i = siblings.length - 1; i >= 0; i--) {
        if (siblings[i].sortOrder >= action.sortOrder) siblings.splice(i, 1);
      }
      siblings.push(action);
      this.rebuild();
      return;
    }

    if (subsequent.length > 0 && this.isTerminal(action)) {
      const confirmed = await this.confirmAsync(
        'Las acciones posteriores se eliminarán porque "Finalizar llamada" cierra el flujo. ¿Continuar?',
        'Insertar Finalizar llamada'
      );
      if (!confirmed) return;
      for (let i = siblings.length - 1; i >= 0; i--) {
        if (siblings[i].sortOrder >= action.sortOrder) siblings.splice(i, 1);
      }
      siblings.push(action);
      this.rebuild();
      return;
    }

    // caso normal: desplazar siblings con sortOrder >= action.sortOrder
    for (const s of siblings) {
      if (s.sortOrder >= action.sortOrder) s.sortOrder++;
    }
    siblings.push(action);
    this.rebuild();
  }

  private confirmAsync(message: string, header: string): Promise<boolean> {
    return new Promise(resolve => {
      this.confirmationService.confirm({
        key: 'dialPlanFlowConfirm',
        message, header,
        acceptLabel: 'Aceptar',
        rejectLabel: 'Cancelar',
        acceptButtonStyleClass: 'p-button-primary',
        icon: 'pi pi-question-circle',
        accept: () => resolve(true),
        reject: () => resolve(false)
      });
    });
  }

  private findBranchInWorking(branchId: string): DialPlanBranchNode | null {
    const walk = (actions: DialPlanActionNode[]): DialPlanBranchNode | null => {
      for (const a of actions) {
        for (const b of a.branches ?? []) {
          if (b.id === branchId) return b;
          const found = walk(b.actions);
          if (found) return found;
        }
      }
      return null;
    };
    return walk(this.workingTree);
  }

  private removeActionFromWorking(actionId: string): boolean {
    const walk = (arr: DialPlanActionNode[]): boolean => {
      const idx = arr.findIndex(a => a.id === actionId);
      if (idx !== -1) { arr.splice(idx, 1); return true; }
      for (const a of arr) {
        for (const b of a.branches ?? []) {
          if (walk(b.actions)) return true;
        }
      }
      return false;
    };
    return walk(this.workingTree);
  }

  private removeBranchFromWorking(branchId: string): boolean {
    const walk = (actions: DialPlanActionNode[]): boolean => {
      for (const a of actions) {
        const idx = (a.branches ?? []).findIndex(b => b.id === branchId);
        if (idx !== -1) { a.branches.splice(idx, 1); return true; }
        for (const b of a.branches ?? []) {
          if (walk(b.actions)) return true;
        }
      }
      return false;
    };
    return walk(this.workingTree);
  }

  deleteAction(action: DialPlanActionNode) {
    // enlaces GoTo que apuntan a esta acción o a cualquiera de su subárbol: quedarían colgantes
    const removedIds = this.collectActionIds(action);
    const dependentLinkIds = this.findGotosTargeting(removedIds)
      .filter(id => !removedIds.has(id)); // los GoTo dentro del subárbol ya se borran con él
    const extra = dependentLinkIds.length
      ? ` Se eliminarán también ${dependentLinkIds.length} enlace(s) que apuntan a esta acción.`
      : '';

    this.confirmationService.confirm({
      key: 'dialPlanFlowConfirm',
      message: '¿Eliminar la acción y todo su subárbol?' + extra,
      header: 'Eliminar acción',
      acceptLabel: 'Aceptar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.removeActionFromWorking(action.id);
        dependentLinkIds.forEach(id => this.removeActionFromWorking(id));
        this.rebuild();
      }
    });
  }

  /** Ids de todas las acciones del subárbol de una acción (la propia incluida). */
  private collectActionIds(action: DialPlanActionNode): Set<string> {
    const ids = new Set<string>();
    const walk = (a: DialPlanActionNode) => {
      ids.add(a.id);
      a.branches?.forEach(b => b.actions.forEach(walk));
    };
    walk(action);
    return ids;
  }

  /** Ids de las acciones GoTo cuyo destino está en el conjunto dado. */
  private findGotosTargeting(targetIds: Set<string>): string[] {
    const result: string[] = [];
    const walk = (acts: DialPlanActionNode[]) => {
      acts.forEach(a => {
        if (a.actionType === 'GoTo' && a.targetActionId && targetIds.has(a.targetActionId)) {
          result.push(a.id);
        }
        a.branches?.forEach(b => walk(b.actions));
      });
    };
    walk(this.workingTree);
    return result;
  }

  deleteBranch(branch: DialPlanBranchNode) {
    this.confirmationService.confirm({
      key: 'dialPlanFlowConfirm',
      message: '¿Eliminar la rama "' + branch.digit + '" y todas sus acciones hijas?',
      header: 'Eliminar rama',
      acceptLabel: 'Aceptar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.removeBranchFromWorking(branch.id);
        this.rebuild();
      }
    });
  }

  startAddBranch(action: DialPlanActionNode) {
    this.addingBranchForAction = action;
    this.newBranchDigit = null;
  }

  cancelAddBranch() {
    this.addingBranchForAction = null;
    this.newBranchDigit = null;
  }

  saveBranch() {
    const action = this.addingBranchForAction;
    if (!action || !this.newBranchDigit) {
      this.messageService.add({ severity: 'warn', detail: 'Seleccione un valor' });
      return;
    }
    if (action.branches.some(b => b.digit === this.newBranchDigit)) {
      this.messageService.add({ severity: 'warn', detail: 'Ya existe esa rama' });
      return;
    }
    const branch: DialPlanBranchNode = {
      id: this.genTmpId(),
      digit: this.newBranchDigit,
      actions: []
    };
    action.branches.push(branch);
    this.cancelAddBranch();
    this.rebuild();
  }

  // -------- mover acción + subárbol --------

  /** Entra en modo mover: la acción y todo su subárbol se reubicarán en el destino elegido. */
  startMove(action: DialPlanActionNode) {
    this.movingAction = action;
    this.movingFromBranchId = this.findParentBranchIdOf(action.id);
    this.movingBranchIds = this.collectBranchIds(action);
    this.cancelAddBranch();
    this.cdr.detectChanges();
  }

  cancelMove() {
    this.movingAction = null;
    this.movingFromBranchId = null;
    this.movingBranchIds.clear();
    this.cdr.detectChanges();
  }

  /** Slot "+" (cola de secuencia / rama vacía) válido como destino del movimiento. */
  isMoveTargetSlot(slot: AddSlotFlowNode): boolean {
    return this.canDropAt(slot.parentBranchId, slot.sortOrder, true);
  }

  /** Botón "+" intermedio sobre una conexión, válido como destino del movimiento. */
  isMoveTargetEdge(edge: FlowEdge): boolean {
    if (!edge.insertAt) return false;
    return this.canDropAt(edge.insertAt.parentBranchId, edge.insertAt.sortOrder, false);
  }

  /**
   * Reglas de destino válido:
   * - ciclo: el destino no puede estar dentro del subárbol que se mueve.
   * - ramificable/terminal: si el nodo movido bifurca o cierra el flujo, solo
   *   puede ir a cola de secuencia o rama vacía (isTailSlot), nunca en medio.
   * - no-op: insertar el nodo justo delante de sí mismo no es un destino real.
   */
  private canDropAt(parentBranchId: string | null, sortOrder: number, isTailSlot: boolean): boolean {
    if (!this.movingAction) return false;
    if (parentBranchId && this.movingBranchIds.has(parentBranchId)) return false;
    const restricted = this.hasBranches(this.movingAction) || this.isTerminal(this.movingAction);
    if (restricted && !isTailSlot) return false;
    if (!isTailSlot && parentBranchId === this.movingFromBranchId && sortOrder === this.movingAction.sortOrder) {
      return false;
    }
    return true;
  }

  /** Saca la acción de su sitio actual y la reinserta (con su subárbol) en el destino. */
  private completeMove(targetBranchId: string | null, targetSortOrder: number) {
    const action = this.movingAction;
    if (!action) return;
    this.removeActionFromWorking(action.id);
    const siblings = this.getSiblingsAt(targetBranchId);
    if (siblings) {
      for (const s of siblings) {
        if (s.sortOrder >= targetSortOrder) s.sortOrder++;
      }
      action.sortOrder = targetSortOrder;
      siblings.push(action);
    }
    this.movingAction = null;
    this.movingFromBranchId = null;
    this.movingBranchIds.clear();
    this.rebuild();
  }

  /** Ids de todas las ramas contenidas en el subárbol de una acción (para evitar ciclos). */
  private collectBranchIds(action: DialPlanActionNode): Set<string> {
    const ids = new Set<string>();
    const walk = (a: DialPlanActionNode) => {
      a.branches?.forEach(b => {
        ids.add(b.id);
        b.actions.forEach(walk);
      });
    };
    walk(action);
    return ids;
  }

  /** Devuelve el id de la rama padre de una acción, o null si está en la raíz. */
  private findParentBranchIdOf(actionId: string): string | null {
    if (this.workingTree.some(a => a.id === actionId)) return null;
    let result: string | null = null;
    const walk = (actions: DialPlanActionNode[]): boolean => {
      for (const a of actions) {
        for (const b of a.branches ?? []) {
          if (b.actions.some(x => x.id === actionId)) { result = b.id; return true; }
          if (walk(b.actions)) return true;
        }
      }
      return false;
    };
    walk(this.workingTree);
    return result;
  }
}
